/**
 * The release-notes wire shape and version ordering, shared by everything
 * that touches `releases/*.md`.
 *
 * Three consumers now agree on this: the portal (which bundles the corpus at
 * build time via `import.meta.glob`), the releases API worker (which bundles a
 * generated JSON projection of the same files), and any client fetching that
 * API. The *parsers* stay separate — one reads `node:fs`, one reads Vite's raw
 * imports — but the ordering rule must not be duplicated a third time, because
 * a portal and an API that sort releases differently would disagree about
 * which one is newest.
 */

/** One release, as published on `/releases` and by `GET /v1/releases/:version`. */
export interface Release {
  readonly version: string;
  readonly date: string;
  readonly headline: string;
  /** Markdown body — `### Added` / `### Fixed` sections and bullet lists. */
  readonly body: string;
}

/** A release without its body, as returned by the list endpoint. */
export type ReleaseSummary = Omit<Release, "body">;

/**
 * Numeric semver comparison — a lexical sort would put 0.1.10 before 0.1.9.
 * Handles an optional `-prerelease` suffix (VERSION's own format allows one,
 * see `scripts/sync-version.mjs`'s validation regex): splitting the whole
 * string on "." and running every segment through `Number` would silently
 * produce `NaN` for a segment like `"0-rc1"`, so the suffix is peeled off
 * before the numeric core is compared. A release without a suffix outranks
 * a prerelease of the same core version, matching normal semver precedence.
 */
export function compareVersions(a: string, b: string): number {
  const [coreA, preA] = splitVersion(a);
  const [coreB, preB] = splitVersion(b);

  for (let i = 0; i < Math.max(coreA.length, coreB.length); i++) {
    const diff = (coreA[i] ?? 0) - (coreB[i] ?? 0);
    if (diff !== 0) return diff;
  }

  if (preA === preB) return 0;
  if (preA === null) return 1; // a is a release, b is a prerelease of the same core
  if (preB === null) return -1;
  return comparePrerelease(preA, preB);
}

/**
 * Prerelease precedence, dot-separated identifier by identifier. Comparing the
 * suffixes as whole strings looks right until a counter reaches two digits:
 * `"rc10" < "rc9"` is true lexically, because `'1'` sorts before `'9'`, which
 * would rank rc10 as *older* than rc9. Each identifier is therefore split into
 * runs of digits and non-digits, and digit runs compare numerically.
 *
 * A shorter identifier list loses to a longer one that shares its prefix
 * (`rc.1` precedes `rc.1.1`), matching semver.
 */
function comparePrerelease(a: string, b: string): number {
  const idsA = a.split(".");
  const idsB = b.split(".");

  for (let i = 0; i < Math.max(idsA.length, idsB.length); i++) {
    const idA = idsA[i];
    const idB = idsB[i];
    if (idA === undefined) return -1;
    if (idB === undefined) return 1;

    const diff = compareIdentifier(idA, idB);
    if (diff !== 0) return diff;
  }

  return 0;
}

/** One identifier, compared as alternating text and number runs — `rc9 < rc10`. */
function compareIdentifier(a: string, b: string): number {
  const runsA = a.match(/\d+|\D+/g) ?? [];
  const runsB = b.match(/\d+|\D+/g) ?? [];

  for (let i = 0; i < Math.max(runsA.length, runsB.length); i++) {
    const runA = runsA[i];
    const runB = runsB[i];
    if (runA === undefined) return -1;
    if (runB === undefined) return 1;

    const numA = /^\d/.test(runA) ? Number(runA) : null;
    const numB = /^\d/.test(runB) ? Number(runB) : null;

    if (numA !== null && numB !== null) {
      if (numA !== numB) return numA < numB ? -1 : 1;
    } else if (numA !== null) {
      return -1; // a numeric run has lower precedence than a text one
    } else if (numB !== null) {
      return 1;
    } else if (runA !== runB) {
      return runA < runB ? -1 : 1;
    }
  }

  return 0;
}

function splitVersion(version: string): [core: number[], prerelease: string | null] {
  const dash = version.indexOf("-");
  const core = dash === -1 ? version : version.slice(0, dash);
  const prerelease = dash === -1 ? null : version.slice(dash + 1);
  return [core.split(".").map(Number), prerelease];
}

/** Newest first — the order every release list is presented in. */
export function sortReleases<T extends { readonly version: string }>(releases: readonly T[]): T[] {
  return [...releases].sort((a, b) => compareVersions(b.version, a.version));
}

/**
 * Look a release up by exact version. Shared for the same reason the ordering
 * is: the portal and the API must agree on what counts as a match, so that a
 * version string resolving on one resolves on the other.
 */
export function findRelease<T extends { readonly version: string }>(
  releases: readonly T[],
  version: string,
): T | undefined {
  return releases.find((r) => r.version === version);
}
